import { NgZone } from '@angular/core';
import * as angular from 'angular';
import * as $ from 'jquery';
import { fromEvent as observableFromEvent, Observable } from 'rxjs';
import { filter, map, scan, takeWhile, timeInterval, withLatestFrom } from 'rxjs/operators';

import { AppStore } from '~/types/main.types';

import { routerSelector } from '../../../reducers/selectors';
import { NotebookSelectors } from '../../../selectors/notebook.selectors';
import { NotebookDataService } from '../../../services/notebook-data-service';

const WINDOW_OFFSET = 50;

function hasModifier(event: KeyboardEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

export function notebookHotkeyDirective(
  $window: angular.IWindowService,
  rxStore: AppStore,
  notebookDataService: NotebookDataService,
  ngZone: NgZone
) {
  return {
    restrict: 'A',
    link: function (scope, element, attrs) {
      const $element = $(element);
      let isAlive = true;

      const notebookToken = scope.ctrl.notebook._resource.token;
      const keyDownRegxForNotebook = /ace_text-input|editor-page|rendered-markdown-output/;

      ngZone.runOutsideAngular(() => {
        const baseKeyStream = observableFromEvent<KeyboardEvent>(document.body, 'keydown').pipe(
          takeWhile(() => isAlive),
          withLatestFrom(rxStore.select(routerSelector)),
          filter(
            ([event, router]) =>
              // Only fire hotkeys when on python editor and there's no modal open (kind of hacky)
              router.name === 'application.reports.edit.notebook' &&
              !$window.document.body.classList.contains('modal-open') && // classList.contains is way faster than $.hasClass
              keyDownRegxForNotebook.test((<HTMLElement>event.target).className)
          ),
          map(([event, router]) => event)
        );

        const doubleKeyStream = baseKeyStream.pipe(
          timeInterval(),
          scan((acc, val) => (val.interval < 250 ? acc.concat(val) : [val]), []),
          filter((val) => val.length === 2),
          map((val) => val.map((v) => v.value))
        );

        doubleKeyStream
          .pipe(
            withLatestFrom(
              rxStore.select(NotebookSelectors.selectedCellToken),
              rxStore.select(NotebookSelectors.isEditing),
              (keyEvents, cellToken, editing) => ({
                keyEvents,
                cellToken,
                editing,
              })
            ),
            filter(({ cellToken, editing }) => cellToken != null && !editing)
          )
          .subscribe(
            ({ keyEvents, cellToken, editing }) => {
              // no modifiers
              if (keyEvents.every((ev) => !hasModifier(ev))) {
                switch (keyEvents.map((ev) => ev.key).join('')) {
                  case 'dd':
                    notebookDataService.removeCell(notebookToken, cellToken);
                    break;
                }
              }
            },
            (err) => console.error(err)
          );

        baseKeyStream
          .pipe(
            withLatestFrom(
              rxStore.select(NotebookSelectors.selectedCellToken),
              rxStore.select(NotebookSelectors.selectedCellFolded),
              rxStore.select(NotebookSelectors.isEditing),
              rxStore.select(NotebookSelectors.selectedCellFrozen),
              rxStore.select(NotebookSelectors.selectedNextCellToken),
              rxStore.select(NotebookSelectors.createCellTokensInNotebookSelector(notebookToken)),
              rxStore.select(NotebookSelectors.createCanCellRunSelector(notebookToken)),
              (
                event,
                selectedCellToken,
                selectedCellFolded,
                isEditing,
                isFrozen,
                nextCellToken,
                notebookCellTokens,
                canRun
              ) => ({
                event,
                notebookCellTokens: notebookCellTokens && notebookCellTokens.toArray(),

                token: selectedCellToken,
                isFolded: selectedCellFolded,
                isFrozen: isFrozen,
                isEditing,
                canRun,
                nextCellToken,
              })
            ),
            filter(({ event, notebookCellTokens, ...selectedCell }) => notebookCellTokens != null)
          )
          .subscribe(
            ({ event, notebookCellTokens, ...selectedCell }) => {
              const selectedIndex = notebookCellTokens.indexOf(selectedCell.token);
              if (selectedIndex < 0) {
                return;
              }

              const containerTop = $element.offset().top;

              switch (event.key) {
                case 'Escape':
                  if (!hasModifier(event) && selectedCell.isEditing) {
                    scope.$evalAsync(() => scope.ctrl.endEditCell(selectedCell.token));
                  }
                  break;
                case 'k': // falls through
                case 'ArrowUp': {
                  if (selectedIndex === 0 || selectedCell.isEditing) {
                    break;
                  }

                  // if there's a modifier that's not the move combo
                  if (!event.altKey && !event.shiftKey && hasModifier(event)) {
                    break;
                  }

                  event.preventDefault();
                  event.stopPropagation();

                  let $next = $('#' + notebookCellTokens[selectedIndex - 1]);
                  if (event.altKey && event.shiftKey) {
                    scope.$evalAsync(() => notebookDataService.moveCell(selectedCell.token, false));
                    $next = $('#' + selectedCell.token);
                  } else {
                    scope.$evalAsync(() =>
                      notebookDataService.selectCell(notebookCellTokens[selectedIndex - 1], false)
                    );
                  }

                  // move into view if top of element is above or below viewing window
                  const topPos = $next.offset().top - containerTop;
                  if (topPos < WINDOW_OFFSET || topPos - $element.height() > WINDOW_OFFSET) {
                    $element.scrollTo($next, { duration: 250, offset: -WINDOW_OFFSET });
                  }

                  break;
                }
                case 'j': // falls through
                case 'ArrowDown': {
                  if (notebookCellTokens.length <= selectedIndex + 1 || selectedCell.isEditing) {
                    break;
                  }

                  // if there's a modifier that's not the move combo
                  if (!event.altKey && !event.shiftKey && hasModifier(event)) {
                    break;
                  }

                  event.preventDefault();
                  event.stopPropagation();

                  let $next = $('#' + notebookCellTokens[selectedIndex + 1]);
                  if (event.altKey && event.shiftKey) {
                    scope.$evalAsync(() => notebookDataService.moveCell(selectedCell.token, true));
                    $next = $('#' + selectedCell.token);
                  } else {
                    scope.$evalAsync(() =>
                      notebookDataService.selectCell(notebookCellTokens[selectedIndex + 1], false)
                    );
                  }

                  // move into view if the bottom of the element is beyond the viewing window
                  const bottomPos = $next.offset().top - containerTop + $next.height();
                  if (bottomPos < WINDOW_OFFSET || bottomPos + WINDOW_OFFSET > $element.height()) {
                    $element.scrollTo($next, { duration: 250, offset: -WINDOW_OFFSET });
                  }
                  break;
                }
                case 'Enter':
                  if (selectedCell.canRun && hasModifier(event)) {
                    if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey) {
                      // run cell
                      event.preventDefault();
                      event.stopPropagation();

                      scope.$evalAsync(() => notebookDataService.runCell(notebookToken, selectedCell.token));
                    } else if (event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
                      // run cell and insert below
                      event.preventDefault();
                      event.stopPropagation();

                      scope.$evalAsync(() => {
                        notebookDataService.runCell(notebookToken, selectedCell.token);
                        notebookDataService.addCell(notebookToken, selectedCell.nextCellToken);
                      });
                    } else if (event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
                      // run and select below, or add cell if it's the last cell
                      event.preventDefault();
                      event.stopPropagation();

                      scope.$evalAsync(() => {
                        notebookDataService.runCell(notebookToken, selectedCell.token);

                        // TODO: Consider a Run cell and select next action
                        if (selectedCell.nextCellToken) {
                          notebookDataService.selectCell(selectedCell.nextCellToken, false);
                        } else {
                          notebookDataService.addCell(notebookToken);
                        }
                      });
                    }
                  } else if (!hasModifier(event) && !selectedCell.isEditing && !selectedCell.isFrozen) {
                    // edit cell
                    scope.$evalAsync(() => notebookDataService.beginEditCell(selectedCell.token));
                  }
                  break;
                case 'a': // insert above
                  if (
                    selectedCell.isEditing ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    $(document.activeElement).hasClass('ace_text-input')
                  ) {
                    break;
                  }

                  scope.$evalAsync(() => notebookDataService.addCell(notebookToken, selectedCell.token));
                  break;
                case 'b': // insert below
                  if (
                    selectedCell.isEditing ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    $(document.activeElement).hasClass('ace_text-input')
                  ) {
                    break;
                  }

                  scope.$evalAsync(() => notebookDataService.addCell(notebookToken, selectedCell.nextCellToken));
                  break;
                case 's': // save cell
                  if (event.metaKey) {
                    event.preventDefault();
                    event.stopPropagation();
                    scope.$evalAsync(() => notebookDataService.saveCell(selectedCell.token));
                  }
                  break;
                case '1':
                  if (event.ctrlKey || event.metaKey) {
                    // add output
                    event.preventDefault();
                    event.stopPropagation();

                    scope.$evalAsync(() => notebookDataService.addCellToLayout(selectedCell.token));
                  }
                  break;
                case '2':
                  if (event.ctrlKey || event.metaKey) {
                    // remove output
                    event.preventDefault();
                    event.stopPropagation();

                    scope.$evalAsync(() => notebookDataService.removeCellFromLayout(selectedCell.token));
                  }
                  break;
              }
            },
            (err) => console.error(err)
          );
      });

      scope.$on('$destroy', () => {
        isAlive = false;
      });
    },
  };
}
